import { useState } from "react";
import { RotateCw } from "lucide-react";

const AREA_LABELS = {
  neck: "Neck",
  upper_back: "Upper Back",
  lower_back: "Lower Back",
  other: "Other",
  shoulder_left: "Shoulder (L)",
  shoulder_right: "Shoulder (R)",
  elbow_left: "Elbow (L)",
  elbow_right: "Elbow (R)",
  wrist_left: "Wrist (L)",
  wrist_right: "Wrist (R)",
  hand_left: "Hand (L)",
  hand_right: "Hand (R)",
  hip_left: "Hip (L)",
  hip_right: "Hip (R)",
};

// In a front-view human silhouette the figure's LEFT side appears on
// the viewer's RIGHT. We label dots from the *figure's* perspective.
const REGIONS_FRONT = [
  { id: "neck", area: "neck", cx: 160, cy: 100, r: 22 },
  { id: "shoulder_l", area: "shoulder_left", cx: 215, cy: 145, r: 24 },
  { id: "shoulder_r", area: "shoulder_right", cx: 105, cy: 145, r: 24 },
  { id: "elbow_l", area: "elbow_left", cx: 245, cy: 240, r: 20 },
  { id: "elbow_r", area: "elbow_right", cx: 75, cy: 240, r: 20 },
  { id: "wrist_l", area: "wrist_left", cx: 270, cy: 320, r: 18 },
  { id: "wrist_r", area: "wrist_right", cx: 50, cy: 320, r: 18 },
  { id: "hand_l", area: "hand_left", cx: 282, cy: 365, r: 18 },
  { id: "hand_r", area: "hand_right", cx: 38, cy: 365, r: 18 },
  { id: "hip_l", area: "hip_left", cx: 190, cy: 340, r: 22 },
  { id: "hip_r", area: "hip_right", cx: 130, cy: 340, r: 22 },
];

const REGIONS_BACK = [
  { id: "neck_b", area: "neck", cx: 160, cy: 100, r: 22 },
  { id: "upper_back", area: "upper_back", cx: 160, cy: 175, r: 30 },
  { id: "lower_back", area: "lower_back", cx: 160, cy: 270, r: 30 },
  { id: "shoulder_lb", area: "shoulder_left", cx: 105, cy: 145, r: 22 },
  { id: "shoulder_rb", area: "shoulder_right", cx: 215, cy: 145, r: 22 },
];

function Silhouette({ side }) {
  return (
    <g>
      <ellipse cx="160" cy="60" rx="36" ry="42" fill="url(#bodyFill)" stroke="#d4d4d8" />
      <rect x="146" y="92" width="28" height="22" rx="6" fill="url(#bodyFill)" stroke="#d4d4d8" />
      <path d="M105 130 Q90 175 92 230 L100 320 Q120 345 160 350 Q200 345 220 320 L228 230 Q230 175 215 130 Z" fill="url(#bodyFill)" stroke="#d4d4d8" />
      <path d="M100 145 Q70 200 70 270 Q70 320 50 380" fill="none" stroke="#d4d4d8" strokeWidth="26" strokeLinecap="round" />
      <path d="M220 145 Q250 200 250 270 Q250 320 270 380" fill="none" stroke="#d4d4d8" strokeWidth="26" strokeLinecap="round" />
      <path d="M118 330 Q160 360 202 330 L210 380 Q160 395 110 380 Z" fill="url(#bodyFill)" stroke="#d4d4d8" />
      <path d="M130 380 Q120 470 128 540" fill="none" stroke="#d4d4d8" strokeWidth="34" strokeLinecap="round" />
      <path d="M190 380 Q200 470 192 540" fill="none" stroke="#d4d4d8" strokeWidth="34" strokeLinecap="round" />
      {side === "back" && (
        <line x1="160" y1="120" x2="160" y2="330" stroke="#e4e4e7" strokeDasharray="3 5" />
      )}
    </g>
  );
}

function OrientationGuide({ side }) {
  // The labels reflect the *figure's* left/right.
  // Front view: figure-left appears on the viewer's right and vice versa.
  // Back view: figure-left appears on the viewer's left.
  const leftX = side === "front" ? 290 : 30;
  const rightX = side === "front" ? 30 : 290;
  return (
    <g className="select-none" pointerEvents="none">
      <text x={leftX} y={28} textAnchor="middle" fontSize="14" fontWeight="800" fill="#71717a">L</text>
      <text x={rightX} y={28} textAnchor="middle" fontSize="14" fontWeight="800" fill="#71717a">R</text>
    </g>
  );
}

// Softened warm gold/amber ramp — capped at ~half of the original intensity so
// no region ever screams; the deepest bucket lands where the "amber" tone used
// to sit. Keeps the whole map feeling calm and on-brand with the site's gold.
const HEATMAP_TONES = {
  none:   { fill: "#f4f4f5", stroke: "#d4d4d8", text: "#71717a" },
  pale:   { fill: "#fef9e6", stroke: "#facc15", text: "#78350f" },
  light:  { fill: "#fef3c7", stroke: "#eab308", text: "#78350f" },
  amber:  { fill: "#fde68a", stroke: "#ca8a04", text: "#78350f" },
  orange: { fill: "#fcd34d", stroke: "#b45309", text: "#78350f" },
  deep:   { fill: "#fbbf24", stroke: "#92400e", text: "#78350f" },
};

export function heatmapLevel(avg) {
  if (avg == null) return "none";
  if (avg >= 7.5) return "deep";
  if (avg >= 6)   return "orange";
  if (avg >= 4.5) return "amber";
  if (avg >= 2.5) return "light";
  return "pale";
}

// Public: color for a given avg pain. Consumed by popups etc. that want to
// tint their background circle to match the clicked region.
export function heatmapColor(avg) {
  return HEATMAP_TONES[heatmapLevel(avg)].fill;
}
export function heatmapStroke(avg) {
  return HEATMAP_TONES[heatmapLevel(avg)].stroke;
}

export default function BodyMap({
  selected = [],
  onChange,
  large = false,
  // Analytics mode: pass a map of `area -> { avg, reports }`. When provided,
  // regions are colored by pain level (red/yellow/green) instead of the
  // gold selection swatch, and clicking a region calls `onRegionClick(area)`
  // instead of toggling `selected`.
  heatmap = null,
  onRegionClick = null,
  activeArea = null,
  // Optional render-prop returning JSX shown as a floating panel anchored to
  // the hovered region. Used by the analytics view to preview a mini
  // pain-vs-stretching chart on hover.
  renderHoverContent = null,
  // Optional temperature-therapy overlay. Pass a map of `area -> { count,
  // cool, warm, both, last_date }`. When present, a subtle ring/badge is
  // drawn on each treated region so the user can see at a glance which sore
  // spots they've been treating vs. neglecting.
  treatments = null,
  title = "Body Map",
  subtitle = "Tap each painful region. Left and right are tracked separately.",
}) {
  const [side, setSide] = useState("front");
  const [hoveredArea, setHoveredArea] = useState(null);
  const regions = side === "front" ? REGIONS_FRONT : REGIONS_BACK;
  const isAnalytics = !!heatmap;
  const hoveredRegion = hoveredArea ? regions.find((r) => r.area === hoveredArea) : null;

  const toggle = (area) => {
    if (isAnalytics) {
      if (onRegionClick) onRegionClick(area);
      return;
    }
    const next = selected.includes(area) ? selected.filter((x) => x !== area) : [...selected, area];
    onChange?.(next);
  };

  return (
    <div className="card-elev">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 className={`font-bold ${large ? "text-2xl" : "text-lg"}`}>{title}</h3>
          <p className="text-xs text-zinc-500">{subtitle}</p>
        </div>
        <div className="inline-flex rounded-md border border-zinc-200 overflow-hidden text-sm shadow-sm">
          <button
            onClick={() => setSide("front")}
            data-testid="body-map-front"
            className={`px-4 py-2 font-semibold transition-colors ${side === "front" ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
          >Front</button>
          <button
            onClick={() => setSide("back")}
            data-testid="body-map-back"
            className={`px-4 py-2 font-semibold transition-colors inline-flex items-center gap-1 ${side === "back" ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
          ><RotateCw className="w-3.5 h-3.5" />Back</button>
        </div>
      </div>

      <div className="relative bg-gradient-to-b from-zinc-50 to-white rounded-2xl border border-zinc-100 overflow-hidden">
        <svg
          viewBox="0 0 320 560"
          className={`w-full h-auto mx-auto block ${large ? "max-h-[720px]" : "max-h-[560px]"}`}
          data-testid={`body-svg-${side}`}
          aria-label={`${side} body map`}
        >
          <defs>
            <linearGradient id="bodyFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#fafafa" />
              <stop offset="100%" stopColor="#f4f4f5" />
            </linearGradient>
            <radialGradient id="painGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#d4af37" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#d4af37" stopOpacity="0" />
            </radialGradient>
          </defs>
          <OrientationGuide side={side} />
          <Silhouette side={side} />

          {regions.map((r) => {
            const isSel = selected.includes(r.area);
            const info = isAnalytics ? (heatmap[r.area] || null) : null;
            const level = isAnalytics ? heatmapLevel(info?.avg) : null;
            const tone = isAnalytics ? HEATMAP_TONES[level] : null;
            const isActive = isAnalytics && activeArea === r.area;
            // Look up treatment info by the region's base name (e.g. "shoulder"
            // for both shoulder_left and shoulder_right). A single log entry
            // for "shoulder" applies to both sides of the map.
            const baseKey = r.area.replace(/_(left|right)$/, "");
            const treat = treatments ? (treatments[baseKey] || treatments[r.area] || null) : null;
            const treatTone = treat
              ? (treat.warm && treat.cool
                  ? { stroke: "#7c3aed", tint: "#e9d5ff" } // both
                  : treat.warm > treat.cool
                    ? { stroke: "#c2410c", tint: "#fed7aa" } // warm-dominant
                    : { stroke: "#2563eb", tint: "#dbeafe" }) // cool-dominant
              : null;
            return (
              <g
                key={r.id}
                className="cursor-pointer"
                onClick={() => toggle(r.area)}
                onMouseEnter={() => isAnalytics && setHoveredArea(r.area)}
                onMouseLeave={() => isAnalytics && setHoveredArea((cur) => (cur === r.area ? null : cur))}
                data-testid={`body-region-${r.area}`}
                role="button"
                aria-pressed={isAnalytics ? isActive : isSel}
                aria-label={AREA_LABELS[r.area]}
              >
                {isAnalytics && level !== "none" && (
                  <circle cx={r.cx} cy={r.cy} r={r.r * 1.6} fill={tone.fill} opacity={0.18} className="pointer-events-none" />
                )}
                {!isAnalytics && isSel && (
                  <circle cx={r.cx} cy={r.cy} r={r.r * 2} fill="url(#painGlow)" className="pointer-events-none" />
                )}
                <circle
                  cx={r.cx}
                  cy={r.cy}
                  r={r.r}
                  className="transition-all duration-150"
                  fill={isAnalytics ? tone.fill : (isSel ? "#d4af37" : "rgba(255,255,255,0.65)")}
                  stroke={isAnalytics ? (isActive ? "#0f172a" : tone.stroke) : (isSel ? "#b5952f" : "#d4d4d8")}
                  strokeWidth={isAnalytics ? (isActive ? 3.5 : 2) : (isSel ? 2.5 : 1.5)}
                  style={{
                    filter: isAnalytics
                      ? (level !== "none" ? `drop-shadow(0 2px 6px ${tone.stroke}66)` : "none")
                      : (isSel ? "drop-shadow(0 2px 6px rgba(212,175,55,0.5))" : "none"),
                  }}
                />
                {/* Temperature-therapy ring — subtle dashed halo around the
                    region so treated spots read at a glance without competing
                    with the pain-intensity fill. */}
                {treat && treatTone && (
                  <>
                    <circle
                      cx={r.cx}
                      cy={r.cy}
                      r={r.r + 5}
                      fill="none"
                      stroke={treatTone.stroke}
                      strokeWidth="1.5"
                      strokeDasharray="3 3"
                      opacity="0.75"
                      className="pointer-events-none"
                    />
                    {/* Tiny corner badge with the treatment count */}
                    <g transform={`translate(${r.cx + r.r * 0.75}, ${r.cy - r.r * 0.85})`} className="pointer-events-none">
                      <circle r="6.5" fill={treatTone.stroke} />
                      <text
                        y="2.5"
                        textAnchor="middle"
                        fontSize="8"
                        fontWeight="800"
                        fill="#ffffff"
                      >
                        {treat.count > 9 ? "9+" : treat.count}
                      </text>
                    </g>
                  </>
                )}
                {isAnalytics && info && info.avg != null && (
                  <text
                    x={r.cx}
                    y={r.cy + 4}
                    textAnchor="middle"
                    fontSize="12"
                    fontWeight="800"
                    fill={tone.text}
                    className="pointer-events-none select-none"
                  >
                    {info.avg}
                  </text>
                )}
                <title>
                  {!isAnalytics ? (AREA_LABELS[r.area] || r.area) : ""}
                </title>
              </g>
            );
          })}
        </svg>
        {isAnalytics && hoveredRegion && renderHoverContent && (
          (() => {
            const xPct = (hoveredRegion.cx / 320) * 100;
            const yPct = (hoveredRegion.cy / 560) * 100;
            // Bias the panel above the region; when the region is in the top
            // third we flip it below so it never falls off-canvas.
            const above = hoveredRegion.cy > 180;
            // Prevent horizontal overflow: if the region is near either edge
            // of the SVG, offset the panel inward so its 220px card stays
            // visible. Anchor is still visually centered via the tail.
            let translateX = "-50%";
            let tailOffset = "50%";
            if (hoveredRegion.cx < 90) {
              translateX = "-10%";
              tailOffset = "20%";
            } else if (hoveredRegion.cx > 230) {
              translateX = "-90%";
              tailOffset = "80%";
            }
            return (
              <div
                className="absolute z-20 pointer-events-none"
                style={{
                  left: `${xPct}%`,
                  top: `${yPct}%`,
                  transform: above
                    ? `translate(${translateX}, calc(-100% - 18px))`
                    : `translate(${translateX}, 18px)`,
                }}
                data-testid={`body-region-hover-${hoveredRegion.area}`}
              >
                <div className="relative">
                  <div className="bg-white border border-zinc-200 rounded-xl shadow-xl px-3 py-2" style={{ width: 220 }}>
                    {renderHoverContent(hoveredRegion.area)}
                  </div>
                  {/* Little pointer tail — kept aligned with the region */}
                  <div
                    className="absolute -translate-x-1/2 w-2.5 h-2.5 rotate-45 bg-white border border-zinc-200"
                    style={above
                      ? { bottom: -5, left: tailOffset, borderTop: "none", borderLeft: "none" }
                      : { top: -5, left: tailOffset, borderBottom: "none", borderRight: "none" }
                    }
                  />
                </div>
              </div>
            );
          })()
        )}
        {selected.length > 0 && !isAnalytics && (
          <div className="absolute bottom-3 left-3 right-3 bg-white/90 backdrop-blur border border-zinc-200 rounded-lg px-3 py-2 text-xs font-semibold text-zinc-700 flex flex-wrap gap-1.5">
            {selected.map((a) => (
              <span key={a} className="px-2 py-0.5 rounded-full text-white" style={{ background: "var(--gold)" }}>{AREA_LABELS[a] || a}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export { AREA_LABELS };
